import { onJwtChanged } from '../hooks';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { setCarlitoHubAccessToken } from '../queries';

const LOCALSTORAGE_CARLITO_HUB_JWT_KEY = 'carlito_hub_jwt';

const CarlitoHubAccessTokenContext = createContext<{
  accessToken: string | null;
  setCarlitoHubAccessToken: React.Dispatch<React.SetStateAction<string | null>>;
  clearCarlitoHubAccessToken: () => void;
}>({
  accessToken: null,
  setCarlitoHubAccessToken: () => {
    // noop
  },
  clearCarlitoHubAccessToken: () => {
    // noop
  },
});

let storedAccessToken: string | null = null;

export function getCarlitoHubAccessToken() {
  if (storedAccessToken === null) {
    storedAccessToken = localStorage.getItem(LOCALSTORAGE_CARLITO_HUB_JWT_KEY);
  }
  return storedAccessToken;
}

const intialJwt = getCarlitoHubAccessToken();
setCarlitoHubAccessToken(intialJwt ?? undefined);

export const CarlitoHubAccessTokenProvider: React.FC<{
  externalJwt?: string;
}> = ({ children, externalJwt }) => {
  const [jwt, setJwt] = useState<string | null>(getCarlitoHubAccessToken());

  useEffect(() => {
    if (externalJwt) return;
    setCarlitoHubAccessToken(jwt ?? undefined);
    if (jwt) {
      localStorage.setItem(LOCALSTORAGE_CARLITO_HUB_JWT_KEY, jwt);
      onJwtChanged();
    }
  }, [externalJwt, jwt]);

  useEffect(() => {
    if (externalJwt) {
      setCarlitoHubAccessToken(externalJwt);
      onJwtChanged();
    }
  }, [externalJwt]);

  const clearCarlitoHubAccessToken = useCallback(() => {
    localStorage.removeItem(LOCALSTORAGE_CARLITO_HUB_JWT_KEY);
    setJwt(null);
    storedAccessToken = null;
    onJwtChanged();
  }, []);

  return (
    <CarlitoHubAccessTokenContext.Provider
      value={{ accessToken: jwt, setCarlitoHubAccessToken: setJwt, clearCarlitoHubAccessToken }}
    >
      {children}
    </CarlitoHubAccessTokenContext.Provider>
  );
};

export const useCarlitoHubAccessToken = () => {
  const context = useContext(CarlitoHubAccessTokenContext);
  if (!context) {
    throw new Error('useCarlitoHubAccessToken must be used within CarlitoHubAccessTokenProvider');
  }

  return context;
};
